import { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, Trash2, Check, RefreshCw, Calendar, Play, Filter } from 'lucide-react';
import { formatDateDisplay } from '@/utils/formatters';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

// Função para verificar se uma data está vencida (sem problemas de timezone)
const isDateOverdue = (dateStr) => {
  if (!dateStr) return false;
  const datePart = dateStr.split('T')[0];
  const [year, month, day] = datePart.split('-').map(Number);
  const dataVencimento = new Date(year, month - 1, day); // mês é 0-indexed
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0); // Zerar horas para comparar apenas datas
  return dataVencimento < hoje;
};

export default function Bills() {
  const [bills, setBills] = useState([]);
  const [pendingWithRecurring, setPendingWithRecurring] = useState([]);
  const [recurringBills, setRecurringBills] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [recurringDialogOpen, setRecurringDialogOpen] = useState(false);
  const [installmentDialogOpen, setInstallmentDialogOpen] = useState(false);
  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [selectedRecurring, setSelectedRecurring] = useState(null);
  const [generateValor, setGenerateValor] = useState('');
  const [payContaId, setPayContaId] = useState('');
  const [payValor, setPayValor] = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  
  // Estado para pagamento dividido
  const [splitPayment, setSplitPayment] = useState(false);
  const [splitPayments, setSplitPayments] = useState([
    { conta_id: '', valor: '' },
    { conta_id: '', valor: '' }
  ]);
  
  // Estado para juros/tarifa no pagamento
  const [tipoAdicionalPagamento, setTipoAdicionalPagamento] = useState('nenhum'); // 'nenhum', 'tarifa', 'juros'
  const [valorAdicionalPagamento, setValorAdicionalPagamento] = useState('');

  const [formData, setFormData] = useState({
    descricao: '',
    valor: '',
    dataVencimento: '',
    categoria: ''
  });

  const [recurringFormData, setRecurringFormData] = useState({
    descricao: '',
    valor: '',
    categoria: '',
    tipo_valor: 'fixo',
    dia_vencimento: '10'
  });

  // Estado para contas parceladas
  const [installmentFormData, setInstallmentFormData] = useState({
    descricao: '',
    valorTotal: '',
    numParcelas: '2',
    dataPrimeiraParcela: '',
    categoria: ''
  });
  const [parcelas, setParcelas] = useState([]);
  const [showParcelasPreview, setShowParcelasPreview] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [billsRes, pendingWithRecurringRes, recurringRes, accountsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/bills`),
        axios.get(`${API}/bills/pending-with-recurring`),
        axios.get(`${API}/recurring-bills`),
        axios.get(`${API}/bank-accounts`),
        axios.get(`${API}/settings/categories/bill`)
      ]);
      setBills(billsRes.data);
      // Usar o endpoint que inclui contas fixas pendentes
      setPendingWithRecurring(pendingWithRecurringRes.data);
      setRecurringBills(recurringRes.data);
      setBankAccounts(accountsRes.data);
      setCategories(categoriesRes.data.categories || []);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/bills`, {
        ...formData,
        valor: parseFloat(formData.valor)
      });
      toast.success('Conta adicionada com sucesso!');
      setDialogOpen(false);
      fetchData();
      setFormData({ descricao: '', valor: '', dataVencimento: '', categoria: '' });
    } catch (error) {
      toast.error('Erro ao adicionar conta');
    }
  };

  const handleRecurringSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/recurring-bills`, {
        ...recurringFormData,
        valor: recurringFormData.tipo_valor === 'fixo' ? parseFloat(recurringFormData.valor) : null,
        dia_vencimento: parseInt(recurringFormData.dia_vencimento)
      });
      toast.success('Conta recorrente criada!');
      setRecurringDialogOpen(false);
      fetchData();
      setRecurringFormData({ descricao: '', valor: '', categoria: '', tipo_valor: 'fixo', dia_vencimento: '10' });
    } catch (error) {
      toast.error('Erro ao criar conta recorrente');
    }
  };

  // Função para gerar preview das parcelas
  const gerarParcelas = () => {
    const { descricao, valorTotal, numParcelas, dataPrimeiraParcela, categoria } = installmentFormData;
    
    if (!descricao || !valorTotal || !numParcelas || !dataPrimeiraParcela) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    const total = parseFloat(valorTotal);
    const qtdParcelas = parseInt(numParcelas);
    const valorParcela = Math.floor((total / qtdParcelas) * 100) / 100; // Arredondar para baixo
    const resto = Math.round((total - (valorParcela * qtdParcelas)) * 100) / 100; // Diferença vai na última

    const novasParcelas = [];
    const dataBase = new Date(dataPrimeiraParcela + 'T12:00:00'); // Meio-dia para evitar problemas de timezone

    for (let i = 0; i < qtdParcelas; i++) {
      const dataParcela = new Date(dataBase);
      dataParcela.setMonth(dataParcela.getMonth() + i);
      
      // Valor da parcela (última recebe o resto)
      let valor = valorParcela;
      if (i === qtdParcelas - 1) {
        valor = valorParcela + resto;
      }

      novasParcelas.push({
        numero: i + 1,
        descricao: `${descricao} - ${i + 1}/${qtdParcelas}`,
        valor: Math.round(valor * 100) / 100,
        dataVencimento: dataParcela.toISOString().split('T')[0],
        categoria: categoria
      });
    }

    setParcelas(novasParcelas);
    setShowParcelasPreview(true);
  };

  // Função para atualizar uma parcela específica
  const atualizarParcela = (index, campo, valor) => {
    const novasParcelas = [...parcelas];
    novasParcelas[index] = {
      ...novasParcelas[index],
      [campo]: campo === 'valor' ? parseFloat(valor) || 0 : valor
    };
    setParcelas(novasParcelas);
  };

  // Função para salvar todas as parcelas
  const handleInstallmentSubmit = async () => {
    if (parcelas.length === 0) {
      toast.error('Gere as parcelas primeiro');
      return;
    }

    try {
      // Criar todas as contas de uma vez
      for (const parcela of parcelas) {
        await axios.post(`${API}/bills`, {
          descricao: parcela.descricao,
          valor: parcela.valor,
          dataVencimento: parcela.dataVencimento,
          categoria: parcela.categoria
        });
      }
      
      toast.success(`${parcelas.length} parcelas criadas com sucesso!`);
      setInstallmentDialogOpen(false);
      setShowParcelasPreview(false);
      setParcelas([]);
      setInstallmentFormData({
        descricao: '',
        valorTotal: '',
        numParcelas: '2',
        dataPrimeiraParcela: '',
        categoria: ''
      });
      fetchData();
    } catch (error) {
      toast.error('Erro ao criar parcelas');
    }
  };

  const handleGenerateBill = async () => {
    if (!selectedRecurring) return;
    try {
      const params = selectedRecurring.tipo_valor === 'variavel' 
        ? `?valor=${parseFloat(generateValor)}`
        : '';
      await axios.post(`${API}/recurring-bills/${selectedRecurring.id}/generate${params}`);
      toast.success('Conta gerada com sucesso!');
      setGenerateDialogOpen(false);
      setSelectedRecurring(null);
      setGenerateValor('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao gerar conta');
    }
  };

  const handlePay = async () => {
    if (!selectedBill) return;
    
    // Se a conta não tem valor definido, exigir que seja informado
    const valorFinal = selectedBill.valor || (payValor ? parseFloat(payValor) : null);
    if (!valorFinal) {
      toast.error('Informe o valor da conta');
      return;
    }

    // Calcular valor adicional (juros/tarifa) se houver
    const valorAdicional = tipoAdicionalPagamento !== 'nenhum' && valorAdicionalPagamento 
      ? parseFloat(valorAdicionalPagamento) 
      : 0;
    
    try {
      // Pagamento dividido
      if (splitPayment) {
        const pagamentosValidos = splitPayments.filter(p => p.conta_id && p.valor);
        if (pagamentosValidos.length < 2) {
          toast.error('Informe pelo menos duas contas para pagamento dividido');
          return;
        }
        
        const totalSplit = pagamentosValidos.reduce((sum, p) => sum + parseFloat(p.valor || 0), 0);
        const valorComAdicional = valorFinal + valorAdicional;
        if (Math.abs(totalSplit - valorComAdicional) > 0.01) {
          toast.error(`A soma dos valores (R$ ${totalSplit.toFixed(2)}) deve ser igual ao valor total (R$ ${valorComAdicional.toFixed(2)})`);
          return;
        }
        
        await axios.put(`${API}/bills/${selectedBill.id}/pay-split`, {
          valor_total: !selectedBill.valor ? valorFinal : null,
          pagamentos: pagamentosValidos.map(p => ({
            conta_id: p.conta_id,
            valor: parseFloat(p.valor)
          })),
          tipo_adicional: tipoAdicionalPagamento !== 'nenhum' ? tipoAdicionalPagamento : null,
          valor_adicional: valorAdicional > 0 ? valorAdicional : null
        });
        toast.success('Conta paga com pagamento dividido!');
      } else {
        // Pagamento normal
        await axios.put(`${API}/bills/${selectedBill.id}/pay`, {
          conta_id: payContaId || null,
          valor: !selectedBill.valor ? valorFinal : null,
          tipo_adicional: tipoAdicionalPagamento !== 'nenhum' ? tipoAdicionalPagamento : null,
          valor_adicional: valorAdicional > 0 ? valorAdicional : null
        });
        toast.success('Conta marcada como paga!');
      }
      
      if (selectedBill.recurring_bill_id) {
        toast.info('Próxima conta gerada automaticamente!');
      }
      setPayDialogOpen(false);
      setSelectedBill(null);
      setPayContaId('');
      setPayValor('');
      setSplitPayment(false);
      setSplitPayments([{ conta_id: '', valor: '' }, { conta_id: '', valor: '' }]);
      setTipoAdicionalPagamento('nenhum');
      setValorAdicionalPagamento('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao marcar conta como paga');
    }
  };

  const handleDeleteRecurring = async (id) => {
    if (!window.confirm('Deseja excluir esta conta recorrente?')) return;
    try {
      await axios.delete(`${API}/recurring-bills/${id}`);
      toast.success('Conta recorrente excluída!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja realmente excluir esta conta?')) return;
    try {
      await axios.delete(`${API}/bills/${id}`);
      toast.success('Conta excluída!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir conta');
    }
  };

  // Filtrar contas por mês
  const filterBillsByMonth = (billsList) => {
    if (!filterMonth) return billsList;
    return billsList.filter(b => {
      const billMonth = b.dataVencimento?.substring(0, 7) || b.dataPagamento?.substring(0, 7);
      return billMonth === filterMonth;
    });
  };

  // Usar o endpoint que inclui contas fixas pendentes (ordenadas por data de vencimento)
  const pendingBills = filterBillsByMonth(pendingWithRecurring);
  const paidBills = filterBillsByMonth(bills.filter(b => b.status === 'pago'));
  const totalPending = pendingBills.reduce((acc, b) => acc + (b.valor || 0), 0);
  const totalPaid = paidBills.reduce((acc, b) => acc + b.valor, 0);

  // Calcular total pendente do mês atual
  const currentMonth = new Date().toISOString().substring(0, 7); // YYYY-MM
  const pendingThisMonth = pendingBills.filter(bill => {
    const billMonth = bill.dataVencimento?.substring(0, 7);
    return billMonth === currentMonth;
  });
  const totalPendingThisMonth = pendingThisMonth.reduce((acc, b) => acc + (b.valor || 0), 0);

  // Gerar lista de meses disponíveis
  const availableMonths = [...new Set(bills.map(b => 
    (b.dataVencimento || b.dataPagamento)?.substring(0, 7)
  ).filter(Boolean))].sort().reverse();

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Carregando...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-4xl font-bold font-manrope tracking-tight">Contas a Pagar</h1>
            <p className="text-muted-foreground mt-1">Gerencie suas despesas</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {/* Filtro por mês */}
            <Select value={filterMonth || 'all'} onValueChange={(v) => setFilterMonth(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-auto min-w-[140px]">
                <Filter className="h-4 w-4 mr-2 flex-shrink-0" />
                <SelectValue placeholder="Filtrar mês" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {availableMonths.map(month => (
                  <SelectItem key={month} value={month}>
                    {new Date(month + '-01').toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Dialog open={recurringDialogOpen} onOpenChange={setRecurringDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Conta Fixa
                </Button>
              </DialogTrigger>
              <DialogContent className="w-full max-w-[95vw] sm:max-w-md max-h-[85vh] overflow-y-auto overflow-x-hidden pb-6 mb-6 sm:mb-0 rounded-2xl sm:rounded-lg">
                <DialogHeader>
                  <DialogTitle className="font-manrope">Nova Conta Fixa Recorrente</DialogTitle>
                  <DialogDescription>
                    Crie uma conta que se repete todo mês. Ao pagar, a próxima será gerada automaticamente.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleRecurringSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Descrição</Label>
                    <Input
                      placeholder="Ex: Aluguel, Internet, Luz..."
                      value={recurringFormData.descricao}
                      onChange={(e) => setRecurringFormData({ ...recurringFormData, descricao: e.target.value })}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Categoria</Label>
                    <Select 
                      value={recurringFormData.categoria} 
                      onValueChange={(value) => setRecurringFormData({ ...recurringFormData, categoria: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione..." />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Tipo de Valor</Label>
                    <Select 
                      value={recurringFormData.tipo_valor} 
                      onValueChange={(value) => setRecurringFormData({ ...recurringFormData, tipo_valor: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fixo">Valor Fixo</SelectItem>
                        <SelectItem value="variavel">Valor Variável</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {recurringFormData.tipo_valor === 'fixo' && (
                    <div className="space-y-2">
                      <Label>Valor Mensal</Label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        value={recurringFormData.valor}
                        onChange={(e) => setRecurringFormData({ ...recurringFormData, valor: e.target.value })}
                        required
                      />
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>Dia de Vencimento</Label>
                    <Select 
                      value={recurringFormData.dia_vencimento} 
                      onValueChange={(value) => setRecurringFormData({ ...recurringFormData, dia_vencimento: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 31 }, (_, i) => i + 1).map(dia => (
                          <SelectItem key={dia} value={dia.toString()}>Dia {dia}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <Button type="submit" className="w-full">Criar Conta Fixa</Button>
                </form>
              </DialogContent>
            </Dialog>

            {/* Dialog de Conta Parcelada */}
            <Dialog open={installmentDialogOpen} onOpenChange={(open) => {
              setInstallmentDialogOpen(open);
              if (!open) {
                setShowParcelasPreview(false);
                setParcelas([]);
              }
            }}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <Calendar className="h-4 w-4 mr-2" />
                  Parcelada
                </Button>
              </DialogTrigger>
              <DialogContent className="w-full max-w-[95vw] sm:max-w-2xl max-h-[80vh] overflow-y-auto overflow-x-hidden pb-6 mb-6 sm:mb-0 rounded-2xl sm:rounded-lg">
                <DialogHeader>
                  <DialogTitle className="font-manrope">Nova Conta Parcelada</DialogTitle>
                  <DialogDescription>
                    Divida uma conta em várias parcelas. Você pode ajustar cada parcela antes de salvar.
                  </DialogDescription>
                </DialogHeader>
                
                {!showParcelasPreview ? (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Descrição</Label>
                      <Input
                        placeholder="Ex: Compra de Materiais"
                        value={installmentFormData.descricao}
                        onChange={(e) => setInstallmentFormData({ ...installmentFormData, descricao: e.target.value })}
                        required
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Valor Total</Label>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          value={installmentFormData.valorTotal}
                          onChange={(e) => setInstallmentFormData({ ...installmentFormData, valorTotal: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Número de Parcelas</Label>
                        <Select 
                          value={installmentFormData.numParcelas} 
                          onValueChange={(v) => setInstallmentFormData({ ...installmentFormData, numParcelas: v })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 18, 24, 36].map(n => (
                              <SelectItem key={n} value={n.toString()}>{n}x</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Data da 1ª Parcela</Label>
                        <Input
                          type="date"
                          value={installmentFormData.dataPrimeiraParcela}
                          onChange={(e) => setInstallmentFormData({ ...installmentFormData, dataPrimeiraParcela: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Categoria</Label>
                        <Select 
                          value={installmentFormData.categoria || 'none'} 
                          onValueChange={(v) => setInstallmentFormData({ ...installmentFormData, categoria: v === 'none' ? '' : v })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Sem categoria</SelectItem>
                            {categories.map(cat => (
                              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {installmentFormData.valorTotal && installmentFormData.numParcelas && (
                      <div className="p-3 bg-muted rounded-lg">
                        <p className="text-sm text-muted-foreground">
                          Valor aproximado por parcela: <strong className="text-foreground">
                            R$ {(parseFloat(installmentFormData.valorTotal) / parseInt(installmentFormData.numParcelas)).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </strong>
                        </p>
                      </div>
                    )}

                    <Button type="button" onClick={gerarParcelas} className="w-full">
                      Gerar Parcelas para Revisão
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <h4 className="font-semibold">Revise e ajuste as parcelas:</h4>
                      <Button variant="outline" size="sm" onClick={() => setShowParcelasPreview(false)}>
                        Voltar
                      </Button>
                    </div>

                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-muted">
                          <tr>
                            <th className="p-2 text-left">Parcela</th>
                            <th className="p-2 text-left">Descrição</th>
                            <th className="p-2 text-left">Vencimento</th>
                            <th className="p-2 text-right">Valor</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {parcelas.map((parcela, index) => (
                            <tr key={index} className="hover:bg-muted/50">
                              <td className="p-2 font-medium">{parcela.numero}/{parcelas.length}</td>
                              <td className="p-2">
                                <Input
                                  value={parcela.descricao}
                                  onChange={(e) => atualizarParcela(index, 'descricao', e.target.value)}
                                  className="h-8 text-sm"
                                />
                              </td>
                              <td className="p-2">
                                <Input
                                  type="date"
                                  value={parcela.dataVencimento}
                                  onChange={(e) => atualizarParcela(index, 'dataVencimento', e.target.value)}
                                  className="h-8 text-sm"
                                />
                              </td>
                              <td className="p-2">
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={parcela.valor}
                                  onChange={(e) => atualizarParcela(index, 'valor', e.target.value)}
                                  className="h-8 text-sm text-right w-28"
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-muted font-semibold">
                          <tr>
                            <td colSpan="3" className="p-2 text-right">Total:</td>
                            <td className="p-2 text-right text-red-500">
                              R$ {parcelas.reduce((acc, p) => acc + p.valor, 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>

                    <div className="flex gap-2">
                      <Button variant="outline" className="flex-1" onClick={() => setInstallmentDialogOpen(false)}>
                        Cancelar
                      </Button>
                      <Button className="flex-1" onClick={handleInstallmentSubmit}>
                        <Check className="h-4 w-4 mr-2" />
                        Criar {parcelas.length} Parcelas
                      </Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="shadow-[0_0_10px_rgba(52,211,153,0.2)]">
                  <Plus className="h-4 w-4 mr-2" />
                  Nova Conta
                </Button>
              </DialogTrigger>
              <DialogContent className="w-full max-w-[95vw] sm:max-w-md max-h-[85vh] overflow-y-auto overflow-x-hidden pb-6 mb-6 sm:mb-0 rounded-2xl sm:rounded-lg">
                <DialogHeader>
                  <DialogTitle className="font-manrope">Nova Conta a Pagar</DialogTitle>
                  <DialogDescription>Adicione uma despesa avulsa</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Descrição</Label>
                    <Input
                      placeholder="Descrição da conta"
                      value={formData.descricao}
                      onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Categoria</Label>
                    <Select 
                      value={formData.categoria} 
                      onValueChange={(value) => setFormData({ ...formData, categoria: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione..." />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Valor</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={formData.valor}
                      onChange={(e) => setFormData({ ...formData, valor: e.target.value })}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Data de Vencimento</Label>
                    <Input
                      type="date"
                      value={formData.dataVencimento}
                      onChange={(e) => setFormData({ ...formData, dataVencimento: e.target.value })}
                      required
                    />
                  </div>

                  <Button type="submit" className="w-full">Adicionar</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-border bg-gradient-to-br from-orange-500/10 to-transparent">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Pendente Este Mês</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold font-mono text-orange-500">
                R$ {totalPendingThisMonth.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {pendingThisMonth.length} conta(s) em {new Date().toLocaleDateString('pt-BR', { month: 'long' })}
              </p>
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Pendente</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold font-mono text-red-500">
                R$ {totalPending.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <p className="text-xs text-muted-foreground mt-1">{pendingBills.length} contas</p>
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Pago</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold font-mono text-green-500">
                R$ {totalPaid.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <p className="text-xs text-muted-foreground mt-1">{paidBills.length} contas</p>
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Contas Fixas</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold font-mono text-blue-500">
                {recurringBills.filter(r => r.ativo).length}
              </div>
              <p className="text-xs text-muted-foreground mt-1">recorrentes ativas</p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="pendentes" className="space-y-4">
          <TabsList>
            <TabsTrigger value="pendentes">Pendentes ({pendingBills.length})</TabsTrigger>
            <TabsTrigger value="pagas">Pagas ({paidBills.length})</TabsTrigger>
            <TabsTrigger value="recorrentes">Contas Fixas ({recurringBills.length})</TabsTrigger>
          </TabsList>

          {/* Pending Bills Tab */}
          <TabsContent value="pendentes">
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="font-manrope">Contas Pendentes</CardTitle>
              </CardHeader>
              <CardContent>
                {pendingBills.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">Nenhuma conta pendente</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-muted/50 text-muted-foreground text-xs uppercase">
                        <tr>
                          <th className="text-left p-3">Descrição</th>
                          <th className="text-left p-3">Categoria</th>
                          <th className="text-left p-3">Vencimento</th>
                          <th className="text-right p-3">Valor</th>
                          <th className="text-center p-3">Ações</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {pendingBills.map(bill => {
                          const isOverdue = isDateOverdue(bill.dataVencimento);
                          const isProlabore = bill.categoria === 'Conta Lucas' || bill.categoria === 'Conta Alexandre';
                          const isRecurringTemplate = bill.is_recurring_template;
                          return (
                            <tr key={bill.id} className={`hover:bg-muted/50 transition-colors ${isOverdue && !isRecurringTemplate ? 'bg-destructive/10' : ''} ${isRecurringTemplate ? 'bg-blue-500/5' : ''}`}>
                              <td className="p-3 font-medium">
                                {bill.descricao}
                                {bill.recurring_bill_id && (
                                  <Badge variant="outline" className={`ml-2 text-xs ${isRecurringTemplate ? 'border-blue-500 text-blue-500' : ''}`}>
                                    <RefreshCw className="h-3 w-3 mr-1" />
                                    {isRecurringTemplate ? 'Fixa (Não gerada)' : 'Fixa'}
                                  </Badge>
                                )}
                              </td>
                              <td className="p-3">
                                {bill.categoria ? (
                                  <Badge variant={isProlabore ? 'default' : 'secondary'} className={isProlabore ? 'bg-purple-600' : ''}>
                                    {bill.categoria}
                                  </Badge>
                                ) : '-'}
                              </td>
                              <td className="p-3">
                                <span className={isOverdue && !isRecurringTemplate ? 'text-destructive font-medium' : ''}>
                                  {formatDateDisplay(bill.dataVencimento)}
                                </span>
                                {isOverdue && !isRecurringTemplate && <span className="text-destructive text-xs ml-1">(Vencida)</span>}
                              </td>
                              <td className="p-3 text-right font-mono font-bold text-destructive">
                                {bill.valor ? `R$ ${bill.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 
                                  <span className="text-muted-foreground font-normal">A definir</span>
                                }
                              </td>
                              <td className="p-3 text-center">
                                <div className="flex justify-center gap-2">
                                  {isRecurringTemplate ? (
                                    <Button 
                                      variant="ghost" 
                                      size="icon" 
                                      onClick={() => {
                                        const recurring = recurringBills.find(r => r.id === bill.recurring_bill_id);
                                        if (recurring) {
                                          setSelectedRecurring(recurring);
                                          setGenerateDialogOpen(true);
                                        }
                                      }} 
                                      title="Gerar conta"
                                    >
                                      <Play className="h-4 w-4 text-blue-500" />
                                    </Button>
                                  ) : (
                                    <>
                                      <Button 
                                        variant="ghost" 
                                        size="icon" 
                                        onClick={() => {
                                          setSelectedBill(bill);
                                          setPayDialogOpen(true);
                                          setTipoAdicionalPagamento('nenhum');
                                          setValorAdicionalPagamento('');
                                        }} 
                                        title="Pagar"
                                      >
                                        <Check className="h-4 w-4 text-primary" />
                                      </Button>
                                      <Button variant="ghost" size="icon" onClick={() => handleDelete(bill.id)} title="Excluir">
                                        <Trash2 className="h-4 w-4 text-destructive" />
                                      </Button>
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Paid Bills Tab */}
          <TabsContent value="pagas">
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="font-manrope">Contas Pagas</CardTitle>
              </CardHeader>
              <CardContent>
                {paidBills.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">Nenhuma conta paga</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-muted/50 text-muted-foreground text-xs uppercase">
                        <tr>
                          <th className="text-left p-3">Descrição</th>
                          <th className="text-left p-3">Categoria</th>
                          <th className="text-left p-3">Vencimento</th>
                          <th className="text-left p-3">Data Pagamento</th>
                          <th className="text-left p-3">Conta</th>
                          <th className="text-right p-3">Valor</th>
                          <th className="text-center p-3">Ações</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {paidBills.map(bill => {
                          const conta = bankAccounts.find(a => a.id === bill.conta_pagamento_id);
                          return (
                            <tr key={bill.id} className="hover:bg-muted/50 transition-colors">
                              <td className="p-3 font-medium">{bill.descricao}</td>
                              <td className="p-3">
                                {bill.categoria ? <Badge variant="secondary">{bill.categoria}</Badge> : '-'}
                              </td>
                              <td className="p-3">{formatDateDisplay(bill.dataVencimento)}</td>
                              <td className="p-3">
                                {bill.dataPagamento ? formatDateDisplay(bill.dataPagamento) : '-'}
                              </td>
                              <td className="p-3">
                                {conta ? <Badge variant="outline">{conta.nome}</Badge> : '-'}
                              </td>
                              <td className="p-3 text-right font-mono">
                                R$ {bill.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                              <td className="p-3 text-center">
                                <div className="flex justify-center gap-2">
                                  <Badge variant="outline" className="bg-primary/10 text-primary border-primary">Pago</Badge>
                                  <Button variant="ghost" size="icon" onClick={() => handleDelete(bill.id)}>
                                    <Trash2 className="h-4 w-4 text-destructive" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Recurring Bills Tab */}
          <TabsContent value="recorrentes">
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="font-manrope flex items-center gap-2">
                  <RefreshCw className="h-5 w-5" />
                  Contas Fixas Recorrentes
                </CardTitle>
              </CardHeader>
              <CardContent>
                {recurringBills.length === 0 ? (
                  <div className="text-center py-8">
                    <RefreshCw className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">Nenhuma conta fixa cadastrada</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-muted/50 text-muted-foreground text-xs uppercase">
                        <tr>
                          <th className="text-left p-3">Descrição</th>
                          <th className="text-left p-3">Categoria</th>
                          <th className="text-left p-3">Tipo</th>
                          <th className="text-right p-3">Valor</th>
                          <th className="text-center p-3">Vencimento</th>
                          <th className="text-center p-3">Status</th>
                          <th className="text-center p-3">Ações</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {recurringBills.map(recurring => (
                          <tr key={recurring.id} className="hover:bg-muted/50 transition-colors">
                            <td className="p-3 font-medium">{recurring.descricao}</td>
                            <td className="p-3">
                              {recurring.categoria ? <Badge variant="secondary">{recurring.categoria}</Badge> : '-'}
                            </td>
                            <td className="p-3">
                              <Badge variant={recurring.tipo_valor === 'fixo' ? 'default' : 'secondary'}>
                                {recurring.tipo_valor === 'fixo' ? 'Fixo' : 'Variável'}
                              </Badge>
                            </td>
                            <td className="p-3 text-right font-mono">
                              {recurring.tipo_valor === 'fixo' 
                                ? `R$ ${recurring.valor?.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                : <span className="text-muted-foreground">A definir</span>
                              }
                            </td>
                            <td className="p-3 text-center">
                              <Badge variant="outline">
                                <Calendar className="h-3 w-3 mr-1" />
                                Dia {recurring.dia_vencimento}
                              </Badge>
                            </td>
                            <td className="p-3 text-center">
                              <Badge variant={recurring.ativo ? 'default' : 'secondary'} className={recurring.ativo ? 'bg-primary' : ''}>
                                {recurring.ativo ? 'Ativa' : 'Inativa'}
                              </Badge>
                            </td>
                            <td className="p-3 text-center">
                              <div className="flex justify-center gap-1">
                                <Button 
                                  variant="ghost" 
                                  size="icon"
                                  onClick={() => {
                                    setSelectedRecurring(recurring);
                                    setGenerateDialogOpen(true);
                                  }}
                                  title="Gerar conta"
                                >
                                  <Play className="h-4 w-4 text-primary" />
                                </Button>
                                <Button variant="ghost" size="icon" onClick={() => handleDeleteRecurring(recurring.id)}>
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Dialog para pagar conta */}
        <Dialog open={payDialogOpen} onOpenChange={setPayDialogOpen}>
          <DialogContent className="w-full max-w-[95vw] sm:max-w-md max-h-[85vh] overflow-y-auto overflow-x-hidden pb-6 mb-6 sm:mb-0 rounded-2xl sm:rounded-lg">
            <DialogHeader>
              <DialogTitle className="font-manrope">Pagar Conta</DialogTitle>
              <DialogDescription>
                Confirme o pagamento de "{selectedBill?.descricao}"
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {/* Se tem valor definido, mostra o valor. Senão, pede para informar */}
              {selectedBill?.valor ? (
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">Valor:</p>
                  <p className="text-2xl font-bold text-red-500">
                    R$ {selectedBill.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Valor da conta (variável)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Informe o valor..."
                    value={payValor}
                    onChange={(e) => setPayValor(e.target.value)}
                    className="text-lg"
                  />
                  <p className="text-xs text-muted-foreground">
                    Esta conta tem valor variável. Informe o valor a pagar.
                  </p>
                </div>
              )}
              
              <div className="space-y-2">
                <Label>De qual conta saiu o dinheiro?</Label>
                <Select value={payContaId || 'none'} onValueChange={(v) => setPayContaId(v === 'none' ? '' : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a conta..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Não informar</SelectItem>
                    {bankAccounts.map(acc => (
                      <SelectItem key={acc.id} value={acc.id}>
                        {acc.nome} (R$ {acc.saldo.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Ao selecionar uma conta, o saldo será atualizado automaticamente
                </p>
              </div>

              {/* Campo de Juros/Tarifa no Pagamento */}
              <div className="space-y-3 p-3 bg-muted/30 rounded-lg border">
                <div className="space-y-2">
                  <Label className="text-sm">Este pagamento teve juros ou tarifa?</Label>
                  <Select 
                    value={tipoAdicionalPagamento} 
                    onValueChange={(value) => {
                      setTipoAdicionalPagamento(value);
                      if (value === 'nenhum') setValorAdicionalPagamento('');
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="nenhum">Nenhum</SelectItem>
                      <SelectItem value="tarifa">Tarifa Bancária</SelectItem>
                      <SelectItem value="juros">Juros</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {tipoAdicionalPagamento !== 'nenhum' && (
                  <div className="space-y-2">
                    <Label className="text-sm">
                      Valor {tipoAdicionalPagamento === 'tarifa' ? 'da Tarifa' : 'dos Juros'}
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={valorAdicionalPagamento}
                      onChange={(e) => setValorAdicionalPagamento(e.target.value)}
                    />
                    {(selectedBill?.valor || payValor) && valorAdicionalPagamento && (
                      <p className="text-xs text-muted-foreground">
                        Total debitado: <span className="font-bold text-red-500">
                          R$ {((selectedBill?.valor || parseFloat(payValor) || 0) + parseFloat(valorAdicionalPagamento)).toFixed(2)}
                        </span>
                        <span className="text-muted-foreground ml-1">
                          (Conta: R$ {(selectedBill?.valor || parseFloat(payValor) || 0).toFixed(2)} + {tipoAdicionalPagamento === 'tarifa' ? 'Tarifa' : 'Juros'}: R$ {parseFloat(valorAdicionalPagamento).toFixed(2)})
                        </span>
                      </p>
                    )}
                  </div>
                )}
              </div>

              {selectedBill?.recurring_bill_id && (
                <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
                  <p className="text-sm text-blue-400">
                    <RefreshCw className="h-4 w-4 inline mr-2" />
                    Esta é uma conta fixa. A próxima será gerada automaticamente.
                  </p>
                </div>
              )}

              {/* Toggle para pagamento dividido */}
              <div className="flex items-center gap-2 pt-2">
                <input
                  type="checkbox"
                  id="split-payment"
                  checked={splitPayment}
                  onChange={(e) => {
                    setSplitPayment(e.target.checked);
                    if (!e.target.checked) {
                      setSplitPayments([{ conta_id: '', valor: '' }, { conta_id: '', valor: '' }]);
                    }
                  }}
                  className="rounded border-border"
                />
                <Label htmlFor="split-payment" className="text-sm cursor-pointer">
                  Dividir pagamento entre contas
                </Label>
              </div>

              {/* Pagamento dividido */}
              {splitPayment && (
                <div className="space-y-3 p-3 bg-muted/50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <p className="text-sm font-medium">Dividir entre:</p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setSplitPayments([...splitPayments, { conta_id: '', valor: '' }])}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Adicionar Conta
                    </Button>
                  </div>
                  {splitPayments.map((payment, index) => (
                    <div key={index} className="flex gap-2 items-center">
                      <Select 
                        value={payment.conta_id || 'none'} 
                        onValueChange={(v) => {
                          const newPayments = [...splitPayments];
                          newPayments[index].conta_id = v === 'none' ? '' : v;
                          setSplitPayments(newPayments);
                        }}
                      >
                        <SelectTrigger className="flex-1">
                          <SelectValue placeholder="Conta..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Selecione...</SelectItem>
                          {bankAccounts.map(acc => (
                            <SelectItem key={acc.id} value={acc.id}>
                              {acc.nome} (R$ {acc.saldo.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Valor"
                        className="w-28"
                        value={payment.valor}
                        onChange={(e) => {
                          const newPayments = [...splitPayments];
                          newPayments[index].valor = e.target.value;
                          setSplitPayments(newPayments);
                        }}
                      />
                      {splitPayments.length > 2 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => {
                            const newPayments = splitPayments.filter((_, i) => i !== index);
                            setSplitPayments(newPayments);
                          }}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <p className="text-xs text-muted-foreground">
                    Total: R$ {splitPayments.reduce((sum, p) => sum + (parseFloat(p.valor) || 0), 0).toFixed(2)}
                    {' / '}
                    R$ {(selectedBill?.valor || parseFloat(payValor) || 0).toFixed(2)}
                  </p>
                </div>
              )}

              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setPayDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button 
                  className="flex-1" 
                  onClick={handlePay}
                  disabled={!selectedBill?.valor && !payValor}
                >
                  <Check className="h-4 w-4 mr-2" />
                  {splitPayment ? 'Pagar Dividido' : 'Confirmar Pagamento'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Dialog para gerar conta */}
        <Dialog open={generateDialogOpen} onOpenChange={setGenerateDialogOpen}>
          <DialogContent className="w-full max-w-[95vw] sm:max-w-md max-h-[85vh] overflow-y-auto overflow-x-hidden pb-6 mb-6 sm:mb-0 rounded-2xl sm:rounded-lg">
            <DialogHeader>
              <DialogTitle className="font-manrope">Gerar Conta</DialogTitle>
              <DialogDescription>
                Gerar conta a pagar de "{selectedRecurring?.descricao}"
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {selectedRecurring?.tipo_valor === 'variavel' ? (
                <div className="space-y-2">
                  <Label>Valor deste mês</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={generateValor}
                    onChange={(e) => setGenerateValor(e.target.value)}
                  />
                </div>
              ) : (
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">Valor fixo:</p>
                  <p className="text-xl font-bold text-green-500">
                    R$ {selectedRecurring?.valor?.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
              )}
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setGenerateDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button 
                  className="flex-1" 
                  onClick={handleGenerateBill}
                  disabled={selectedRecurring?.tipo_valor === 'variavel' && !generateValor}
                >
                  <Play className="h-4 w-4 mr-2" />
                  Gerar Conta
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
